import { useCallback } from "react";

import { usePageActions } from "@/modules/pages/hooks/usePageActions";
import { useWidgetActions } from "@/modules/widgets/hooks/useWidgetActions";
import { usePage } from "@/modules/pages";
import { Widget, widgetEndpoints } from "@/modules/widgets";
import { useDashboard, useDashboardSelections } from "@/modules/dashboard";
import { flushSync } from "react-dom";

/**
 * Global hook for widget updates
 * Coordinates state updates across involved modules
 */

export function useWidgetGlobalOperations() {
    const {
        state: { currentDashboard },
    } = useDashboard();
    const {
        state: { currentPage, currentPageWidgetsData },
    } = usePage();
    const { selectedDateRange } = useDashboardSelections();
    const { updatePage, updatePageWidget, setLoading: setPageLoading, deletePageWidget } = usePageActions();
    const { addWidget: addWidgetAction, deleteWidget: deleteWidgetAction, updateWidget: updateWidgetAction, setLoading: setWidgetLoading } = useWidgetActions();

    const createWidget = useCallback(
        async (prompt: string) => {
            try {
                if (!currentDashboard || !currentPage) return;
                setPageLoading(true);
                const response = await widgetEndpoints.createWidget({
                    prompt,
                    clientId: currentDashboard.clientId,
                    dashboardId: currentDashboard.id,
                    pageId: currentPage.id,
                });

                if (response) {
                    updatePage({
                        ...currentPage,
                        widgets: [...(currentPage?.widgets ?? []), response.widget],
                    });

                    addWidgetAction(response.widget);
                }
            } catch (error) {
                console.log("Widget Global Operations", error);
            } finally {
                setPageLoading(false);
            }
        },
        [currentDashboard, currentPage, setPageLoading, updatePage, addWidgetAction]
    );

    const deleteWidget = useCallback(
        async (widget: Widget) => {
            try {
                console.log("### deleteWidget", { widgetId: widget.id, pageId: widget.pageId });
                await widgetEndpoints.deleteWidget({ widgetId: widget.id });
                flushSync(() => {
                    deletePageWidget(widget.pageId, widget.id);
                });
                deleteWidgetAction(widget.id);
            } catch (error) {
                console.log("Widget Global Operations", error);
                window.location.reload();
            } finally {
                setPageLoading(false);
            }
        },
        [deleteWidgetAction, deletePageWidget, setPageLoading]
    );

    const addWidget = useCallback(
        async (widget: Widget) => {
            try {
                if (!currentDashboard || !currentPage) return;

                updatePage({
                    ...currentPage,
                    widgets: [...(currentPage?.widgets ?? []), widget],
                });

                addWidgetAction(widget);
            } catch (error) {
                console.log("Widget Global Operations", error);
            } finally {
                setPageLoading(false);
            }
        },
        [currentDashboard, currentPage, addWidgetAction, setPageLoading]
    );

    const updateWidget = useCallback(
        async (widget: Widget) => {
            setWidgetLoading(true);

            try {
                if (!currentDashboard || !currentPage) return;

                updateWidgetAction(widget);
                updatePageWidget(widget.pageId, widget);

                updatePage({
                    ...currentPage,
                    widgets: (currentPage.widgets || []).map((w) => (w.id === widget.id ? widget : w)),
                });
            } catch (error) {
                console.log("Widget Global Operations", error);
            } finally {
                setWidgetLoading(false);
            }
        },
        [currentDashboard, currentPage, updateWidgetAction, updatePageWidget, updatePage, setWidgetLoading]
    );

    const reloadDateSensitiveWidgets = useCallback(async () => {
        if (!selectedDateRange || !currentPageWidgetsData) return;

        setWidgetLoading(true);

        try {
            // Find widgets that use dashboard_dates as their date period
            // Note: This would need to be implemented based on widget configuration
            const dateWidgets = Object.values(currentPageWidgetsData).filter((widget) => (widget as any).datePeriod === "dashboard_dates");
            console.log(`🔄 Reloading ${dateWidgets.length} date-sensitive widgets with new date range:`, selectedDateRange);

            // Reload each date-sensitive widget
            // Note: This would require a widget reload endpoint that accepts new date parameters
            // For now, we'll just log the action
            for (const widget of dateWidgets) {
                console.log(`📊 Would reload widget: ${widget.title} (ID: ${widget.id})`);
                // TODO: Implement actual widget reload with new date range
                // await widgetEndpoints.reloadWidget(widget.id, { dateRange: selectedDateRange });
            }
        } catch (error) {
            console.error("Error reloading date-sensitive widgets:", error);
        } finally {
            setWidgetLoading(false);
        }
    }, [selectedDateRange, currentPageWidgetsData, setWidgetLoading]);

    return {
        createWidget,
        deleteWidget,
        addWidget,
        updateWidget,
        reloadDateSensitiveWidgets,
    };
}
